import React, { useContext } from 'react';
import { Stack, Text, IStackTokens } from '@fluentui/react';
import {
    StackedBarChart,
    IChartDataPoint,
    IChartProps,
} from '@fluentui/react-charting';
import { ThemeContext } from '../../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../../Common/util/localStorageUtils';
import { useIsMobile } from '../../../../../Common/hooks/useIsMobile';
import {
    getStatusIconProperties,
    getTimelineProgress,
    getTimelineStepText,
    isFinalStepStatus,
    TimelineItem,
} from '../../../../models/Timeline';
import {
    calculateTimeDifference,
    dateLocaleStringWithTimeZone,
} from '../../../../../Common/util/DateUtils';
import { TimelineChartLegendItem } from './TimelineChartLegendItem';

interface ITimelineChartProps {
    currentFlightTimeline: TimelineItem[] | undefined;
}

export const TimelineChart: React.FC<ITimelineChartProps> = (props) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();
    const stackTokens: IStackTokens = { childrenGap: 10 };

    if (
        !props.currentFlightTimeline ||
        props.currentFlightTimeline.length === 0
    ) {
        return null;
    }

    // Show all items that should be displayed
    const visibleItems = props.currentFlightTimeline.filter(
        (item) => item.shouldDisplay
    );

    if (visibleItems.length === 0) {
        return null;
    }

    // Find the step that is running, and the terminal step that stopped the run if it ended early
    const { inProgressIndex, endedAtIndex } = getTimelineProgress(visibleItems);
    const runEndedEarly = endedAtIndex !== -1;

    // Determine the end timestamp for duration calculation
    const endTimestamp = runEndedEarly
        ? visibleItems[endedAtIndex].timestamp
        : visibleItems[visibleItems.length - 1].timestamp ||
          new Date().getTime();

    const totalDuration = calculateTimeDifference(
        visibleItems[0].timestamp,
        endTimestamp
    );

    return (
        <div>
            <Stack tokens={stackTokens}>
                <Stack tokens={{ childrenGap: 5 }}>
                    {/* Duration Header */}
                    <Stack
                        horizontal={!isMobile}
                        horizontalAlign={isMobile ? 'start' : 'space-between'}
                        wrap={!isMobile}
                        tokens={{ childrenGap: isMobile ? 5 : 10 }}
                        style={{
                            padding: '10px 0',
                        }}
                    >
                        <Text variant="medium" style={{ fontWeight: 600 }}>
                            Total Duration: {totalDuration}
                        </Text>
                        <Text
                            variant={isMobile ? 'small' : 'medium'}
                            style={{
                                color: theme.palette.neutralSecondary,
                            }}
                        >
                            {dateLocaleStringWithTimeZone(
                                visibleItems[0].timestamp
                            )}{' '}
                            -{' '}
                            {runEndedEarly
                                ? dateLocaleStringWithTimeZone(
                                      visibleItems[endedAtIndex].timestamp
                                  )
                                : visibleItems[visibleItems.length - 1]
                                      .timestamp
                                ? dateLocaleStringWithTimeZone(
                                      visibleItems[visibleItems.length - 1]
                                          .timestamp
                                  )
                                : 'Processing'}
                        </Text>
                    </Stack>

                    {/* Timeline Bars */}
                    {(() => {
                        // Transform timeline events into valid data points
                        const chartPoints = visibleItems
                            .map((item, index) => {
                                // The run ended early, so the step after the terminal one never
                                // started - drop it rather than charting it as running
                                if (
                                    runEndedEarly &&
                                    index === endedAtIndex + 1
                                ) {
                                    return null;
                                }

                                let itemTimestamp = item.timestamp;
                                let durationMinutes = 0;
                                let formattedDuration = '';

                                if (index > 0) {
                                    // For items without a timestamp (pending/in progress), use current time
                                    const useCurrentTime =
                                        !itemTimestamp || itemTimestamp === '';
                                    if (useCurrentTime) {
                                        itemTimestamp =
                                            new Date().toISOString();
                                    }

                                    const currentMs = new Date(
                                        itemTimestamp
                                    ).getTime();
                                    const prevMs = new Date(
                                        visibleItems[index - 1].timestamp
                                    ).getTime();

                                    // Only calculate if both timestamps are valid
                                    if (!isNaN(currentMs) && !isNaN(prevMs)) {
                                        durationMinutes =
                                            (currentMs - prevMs) / (1000 * 60); // Convert to minutes
                                        formattedDuration =
                                            calculateTimeDifference(
                                                visibleItems[index - 1]
                                                    .timestamp,
                                                itemTimestamp
                                            );
                                    }
                                }

                                let color: string;
                                let calloutLabel: string = '';
                                if (item.status !== 'NotStarted') {
                                    color =
                                        getStatusIconProperties(
                                            item.status,
                                            theme
                                        ).iconColor ||
                                        theme.palette.themePrimary;
                                } else if (
                                    item.status === 'NotStarted' &&
                                    index === inProgressIndex
                                ) {
                                    // First NotStarted -- Label as InProgress
                                    color = theme.palette.themePrimary;
                                } else {
                                    // Pending (nothing after this is running)
                                    color = theme.palette.neutralTertiary;
                                    calloutLabel = 'Upcoming';
                                }

                                return {
                                    legend: `${
                                        getTimelineStepText(
                                            item.name,
                                            item.description,
                                            index === inProgressIndex,
                                            isFinalStepStatus(item.status),
                                            item.pendingName,
                                            item.pendingDescription
                                        ).name
                                    }${item.cloud ? ` (${item.cloud})` : ''}`,
                                    data: Math.max(durationMinutes, 1),
                                    color: color,
                                    yAxisCalloutData:
                                        calloutLabel || formattedDuration,
                                };
                            })
                            .filter(
                                (point) => point !== null
                            ) as IChartDataPoint[];

                        const chartData: IChartProps = {
                            chartTitle: 'Duration',
                            chartData: chartPoints,
                        };

                        return (
                            <div style={{ marginBottom: 1 }}>
                                <StackedBarChart
                                    data={chartData}
                                    hideLegend={true}
                                    hideTooltip={false}
                                    barHeight={18}
                                />
                            </div>
                        );
                    })()}

                    {/* Legend */}
                    <Stack
                        horizontal
                        horizontalAlign="center"
                        tokens={{ childrenGap: 20 }}
                        style={{ paddingTop: 10 }}
                    >
                        <TimelineChartLegendItem
                            backgroundColor={theme.palette.green}
                            label="Completed"
                        />
                        <TimelineChartLegendItem
                            backgroundColor={theme.palette.themePrimary}
                            label="Processing"
                        />
                        <TimelineChartLegendItem
                            backgroundColor={theme.palette.neutralTertiary}
                            label="Upcoming"
                        />
                        <TimelineChartLegendItem
                            backgroundColor={theme.semanticColors.errorIcon}
                            label="Failed"
                        />
                    </Stack>
                </Stack>
            </Stack>
        </div>
    );
};
